import type {
  FileAudioUsage,
  FileImageUsage,
  FileUsage,
  FileVideoUsage,
} from '@vedalib/editor/lib/files'
import { FileUsageSource, FileUsageTypeEnum, ObjectFitEnum } from '@vedalib/editor/lib/files'
import cn from 'classnames'
import lodash from 'lodash'
import numeral from 'numeral'
import { useCallback, useEffect, useState } from 'react'

import ImageEditModal from 'components/modals/ImageEditModal'
import type { IKitControlProps } from 'components/uiKit/KitTypes'
import { KitSize } from 'components/uiKit/KitTypes'
import { isMixed } from 'components/uiKit/KitUtils'
import { getFileMetaByIdFromCache } from 'gql/files/apollo'
import { OBJECT_FIT_OPTIONS } from 'services/Branding/constants'
import { t } from 'services/Translation'
import { useUrlByUsage } from 'utils/files'

import Button, { IconButton } from '../Button'
import { Tooltip } from '../Dropdown'
import Icon from '../Icon'
import Input from '../Input'
import { useOpenModal } from '../Modal'
import Segmented from '../Segmented'
import Select from '../Select'
import * as s from './FileLoader.module.scss'
import AudioPreview from './components/AudioPreview'
import VideoPreview from './components/VideoPreview/VideoPreview'
import { useFileHandlers } from './useFileHandlers'

export type FileTypes = 'image' | 'audio' | 'video' | 'document' | 'captions'
export interface IFieldFileCommon {
  fileType: FileTypes
  placeholder?: string
  nullable?: boolean
  preview?: boolean
  showSource?: boolean
  hasObjectFit?: boolean
  hasAccessibility?: boolean
}

const isFileUsage = (value: FileUsage | null): value is FileUsage => !!value?.id

const isImageUsage = (value: FileUsage | null, fileType: FileTypes): value is FileImageUsage => {
  return (!!value?.id && value?.type === 'image') || fileType === 'image'
}

export const isFileType = (type: FileTypes, currentType: FileTypes) => type === currentType

const SOURCE_OPTIONS = [
  { label: t('input.option.file'), value: FileUsageSource.file },
  { label: t('input.option.link'), value: FileUsageSource.url },
]

export interface IFileLoaderProps<V extends FileUsage | null> extends IKitControlProps<V> {
  fileType: FileTypes
  preview?: boolean
  // Should only be responsible for the presence of a reset button,
  // For some reason, it was also responsible for editing, as part of working with section background:
  // (https://www.notion.so/Branding-Section-background-22b5457f94c5805da984f4fec2d1c28a)
  // Added a separate prop editable = true to avoid breaking existing functionality
  nullable?: boolean
  editable?: boolean
  placeholder?: string
  showSource?: boolean
  hasObjectFit?: boolean
  hasAccessibility?: boolean
}

const isValueEmpty = (value: FileUsage | null) => !value?.id && !value?.url

const FileLoader = <V extends FileUsage | null>({
  defaultValue,
  value: valuePropRaw = null as V,
  fileType,
  onBlur,
  onFocus,
  onChange,
  name,
  showSource,
  hasObjectFit,
  hasAccessibility,
  editable = true,
  ...rest
}: IFileLoaderProps<V>) => {
  const valueProp = isMixed(valuePropRaw) ? (null as V) : (valuePropRaw as V)
  const safeDefault = isMixed(defaultValue) ? (null as V) : (defaultValue as V | undefined)
  const value = (isValueEmpty(valueProp) ? safeDefault : valueProp) || valueProp
  const { nullable, placeholder, preview, disabled } = rest
  const { source = FileUsageSource.file, url, id } = value || {}

  const { handlePickFile, handleResetFile, handleChangeUrl, handleChangeObjectFit } =
    useFileHandlers({
      onChange,
      value,
      onFocus,
      onBlur,
      fileType,
    })
  const file = getFileMetaByIdFromCache(isFileUsage(value) ? id : undefined)
  const size = numeral(file?.size).format('0.0 b')
  const { name: fileName } = file || {}
  const imageUrl = useUrlByUsage(value, FileUsageTypeEnum.image)

  const [localSource, setLocalSource] = useState(source)

  const video = isFileType(fileType, 'video')
  const image = isFileType(fileType, 'image')
  const audio = isFileType(fileType, 'audio')
  const document = isFileType(fileType, 'document')
  const fileSource = localSource === FileUsageSource.file
  const urlSource = localSource === FileUsageSource.url
  const hasImage = (fileSource && id) || (urlSource && url)
  const emptyFile = !(fileSource && id) && !(urlSource && url)
  const emptyDescription = fileSource
    ? t('uiKit.fileLoader.placeholder')
    : t('uiKit.fileLoader.urlHint')

  const editFileImage = useOpenModal(ImageEditModal, { image: value as FileImageUsage })

  const handleSetLocalSource = useCallback(
    (source: FileUsageSource) => {
      setLocalSource(source)
      if (source === FileUsageSource.url && value?.url) {
        onChange?.({ ...value, source })
      }
      if (source === FileUsageSource.file && value?.id) {
        onChange?.({ ...value, source })
      }
    },
    [onChange, value],
  )

  const openCropModal = async () => {
    const result = await editFileImage.open({})
    if (result) {
      const resultValue = lodash.omitBy(result, lodash.isUndefined)
      if (urlSource) {
        const newValue = {
          url,
          accessibility: resultValue?.accessibility,
          source: FileUsageSource.url,
          objectFit: (value as FileImageUsage)?.objectFit,
        }
        onChange?.(newValue as V)
      } else if (isImageUsage(value, fileType)) {
        const { cropParams } = lodash.cloneDeep(lodash.omit(resultValue, ['accessibility']))
        const newValue = {
          id,
          params: cropParams,
          accessibility: resultValue?.accessibility,
          source: FileUsageSource.file,
          objectFit: (value as FileImageUsage)?.objectFit,
        }
        onChange?.(newValue as V)
      }
    }
  }

  useEffect(() => {
    if (file == null && id && source === FileUsageSource.file) {
      onChange?.({ ...value, id: null, source: FileUsageSource.file, params: undefined } as V)
    }
  }, [source, file, id, onChange, value])

  return (
    <div className={s.root}>
      {showSource && (
        <Segmented
          name={`${name}-source`}
          onChange={handleSetLocalSource}
          options={SOURCE_OPTIONS}
          size={KitSize.S}
          value={localSource}
        />
      )}
      <div className={s.wrapper}>
        {preview && !document && fileType && (
          <div
            className={cn(s.preview, { [s.full]: image || video, [s.empty]: !id && fileSource })}
            onClick={!id && fileSource ? () => handlePickFile() : undefined}
          >
            {image && hasImage && imageUrl && <img src={imageUrl} />}
            {video && hasImage && <VideoPreview {...(value as FileVideoUsage)} />}
            {audio && <AudioPreview {...(value as FileAudioUsage)} />}
            {emptyFile && !audio && (
              <Icon
                className={cn(s.icon, { [s.active]: emptyFile })}
                name={fileSource ? 'add' : 'link2'}
              />
            )}
          </div>
        )}
        <div className={s.info}>
          {fileSource ? (
            <Tooltip overlay={fileName || undefined}>
              <div className={s.title}>{fileName}</div>
            </Tooltip>
          ) : (
            <Input
              disabled={disabled}
              name={name}
              onChange={handleChangeUrl}
              placeholder={placeholder || t('input.placeholder.pasteLink')}
              size={KitSize.S}
              value={url}
              autoFocus
            />
          )}
          <div
            className={cn(s.description, { [s.bold]: !file && fileSource, [s.link]: !fileSource })}
          >
            {fileSource && file ? size : emptyDescription}
          </div>
          {fileSource && (
            <div className={s.footer}>
              <div className={s.buttons}>
                <Button
                  disabled={disabled}
                  name={`${name}.replace`}
                  onClick={handlePickFile}
                  size={KitSize.S}
                  styleType='secondary'
                >
                  {file ? t('uiKit.button.replace') : t('uiKit.button.upload')}
                </Button>
                {/* // why nullable ? */}
                {file && image && nullable && editable && (
                  <Button
                    disabled={disabled}
                    name={`${name}.crop`}
                    onClick={openCropModal}
                    size={KitSize.S}
                    styleType='secondary'
                  >
                    {t('modal.editorCrop.title')}
                  </Button>
                )}
              </div>
              {file && nullable && (
                <IconButton
                  disabled={disabled}
                  icon='trashDeleteBin'
                  name={`${name}.delete`}
                  onClick={handleResetFile}
                  size={KitSize.S}
                  styleType='ghost'
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        {!emptyFile && hasAccessibility && (
          <div className={s.fields}>
            <div className={s.label}>{t('input.label.accessibility')}</div>
            <div>
              <span className={s.editButton} onClick={openCropModal}>
                {t('uiKit.fileLoader.altText')}
              </span>
            </div>
          </div>
        )}
        {!emptyFile && hasObjectFit && (
          <div className={s.fields}>
            <div className={s.label}>{t('input.label.objectFit')}</div>
            <Tooltip overlay={<div>{t('input.tooltip.scaling')}</div>}>
              <Icon className={s.infoIcon} name='builderTooltip' size={KitSize.S} />
            </Tooltip>
            <Select
              defaultValue={ObjectFitEnum.contain}
              name={`${name}.objectFit`}
              onChange={handleChangeObjectFit}
              options={OBJECT_FIT_OPTIONS}
              size={KitSize.S}
              value={(value as FileImageUsage)?.objectFit}
              fluid
            />
          </div>
        )}
      </div>
    </div>
  )
}
export default FileLoader
